/**
 * Contractor Interest record + dedupe (P3)
 *
 * When a contractor in limited_exposure mode clicks "Request Offer" on a
 * visible job, an Interest record is created. Interest is NOT an offer —
 * it does not change job state, does not assign anyone, and CANNOT be
 * auto-promoted to an offer. The dispatcher reviews the interest queue
 * and manually clicks "Send Offer" through the existing offer engine.
 *
 * Locked spec:
 *   - One active interest per (contractor, job).
 *   - Duplicate submission returns the existing record + emits NO event.
 *   - Closes when:
 *       * dispatcher sends an offer to this contractor for this job
 *       * dispatcher dismisses
 *       * job leaves dispatch-eligible state (assigned / completed / etc.)
 *
 * Pure functions. Caller owns persistence + event emission.
 */

import type { SystemEvent } from "@/data/communicationEngine";
import { createSystemEvent } from "@/data/eventFactory";
import type { Job } from "@/data/types";
import type { MarketplaceOffer } from "./marketplaceOffer";
import { DISPATCH_ELIGIBLE_STAGES } from "./opportunityVisibility";

export type InterestStatus = "open" | "offer_sent" | "dismissed" | "stale";

export type InterestCloseReason =
  | "offer_sent"
  | "dispatcher_dismissed"
  | "job_left_dispatch";

export interface ContractorInterest {
  id: string;
  jobId: string;
  contractorId: string;
  submittedAt: string;
  status: InterestStatus;
  closedAt?: string;
  closeReason?: InterestCloseReason;
}

export interface InterestSubmissionResult {
  interests: ContractorInterest[];
  events: SystemEvent[];
  /** The active interest record (existing if dedup, fresh otherwise). */
  interest: ContractorInterest;
  /** True when a new record was created (i.e., not a duplicate). */
  created: boolean;
}

function newId(): string {
  return `int-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

/** Submit interest. Idempotent on (contractor, job) when an open record exists. */
export function submitInterest(
  interests: ContractorInterest[],
  input: { jobId: string; contractorId: string; actor: string },
): InterestSubmissionResult {
  const existing = interests.find(
    (i) =>
      i.jobId === input.jobId &&
      i.contractorId === input.contractorId &&
      i.status === "open",
  );
  if (existing) {
    // Idempotent: no event, no new record.
    return { interests, events: [], interest: existing, created: false };
  }

  const fresh: ContractorInterest = {
    id: newId(),
    jobId: input.jobId,
    contractorId: input.contractorId,
    submittedAt: nowIso(),
    status: "open",
  };

  const event = createSystemEvent({
    type: "contractor_interest_submitted",
    jobId: input.jobId,
    triggeredBy: input.actor,
    payload: {
      interestId: fresh.id,
      contractorId: input.contractorId,
    },
  });

  return {
    interests: [...interests, fresh],
    events: [event],
    interest: fresh,
    created: true,
  };
}

interface CloseResult {
  interests: ContractorInterest[];
  events: SystemEvent[];
  closedCount: number;
}

function closeInterests(
  interests: ContractorInterest[],
  predicate: (i: ContractorInterest) => boolean,
  status: Exclude<InterestStatus, "open">,
  reason: InterestCloseReason,
  actor: string,
): CloseResult {
  const at = nowIso();
  const events: SystemEvent[] = [];
  let closedCount = 0;
  const next = interests.map((i) => {
    if (i.status !== "open" || !predicate(i)) return i;
    closedCount++;
    events.push(
      createSystemEvent({
        type: "contractor_interest_closed",
        jobId: i.jobId,
        triggeredBy: actor,
        payload: {
          interestId: i.id,
          contractorId: i.contractorId,
          reason,
        },
      }),
    );
    return { ...i, status, closedAt: at, closeReason: reason };
  });
  return { interests: next, events, closedCount };
}

/** Dispatcher dismissed a specific interest. */
export function dismissInterest(
  interests: ContractorInterest[],
  interestId: string,
  actor: string,
): CloseResult {
  return closeInterests(
    interests,
    (i) => i.id === interestId,
    "dismissed",
    "dispatcher_dismissed",
    actor,
  );
}

/** Marks any open interest matching the (job, contractor) as offer_sent. */
export function closeInterestForOfferSent(
  interests: ContractorInterest[],
  jobId: string,
  contractorId: string,
  actor: string,
): CloseResult {
  return closeInterests(
    interests,
    (i) => i.jobId === jobId && i.contractorId === contractorId,
    "offer_sent",
    "offer_sent",
    actor,
  );
}

/**
 * Sweeps interests for jobs that are no longer dispatch-eligible (assigned,
 * completed, or any non-dispatch stage). Closes them with reason
 * job_left_dispatch. Idempotent — running twice closes nothing the second time.
 */
export function sweepStaleInterests(
  interests: ContractorInterest[],
  jobs: Job[],
  offers: MarketplaceOffer[],
  actor: string = "system",
): CloseResult {
  const jobsById = new Map(jobs.map((j) => [j.id, j]));
  const acceptedJobIds = new Set(
    offers.filter((o) => o.status === "accepted").map((o) => o.jobId),
  );
  return closeInterests(
    interests,
    (i) => {
      const j = jobsById.get(i.jobId);
      if (!j) return true;
      if (j.assignedSubcontractorId) return true;
      if (acceptedJobIds.has(j.id)) return true;
      if (!DISPATCH_ELIGIBLE_STAGES.has(j.currentStage)) return true;
      return false;
    },
    "stale",
    "job_left_dispatch",
    actor,
  );
}

/* ── Selectors ─────────────────────────────────────────────────────── */

export function getOpenInterestsForJob(
  interests: ContractorInterest[],
  jobId: string,
): ContractorInterest[] {
  return interests
    .filter((i) => i.jobId === jobId && i.status === "open")
    .sort((a, b) => a.submittedAt.localeCompare(b.submittedAt));
}

export function getOpenInterestsForContractor(
  interests: ContractorInterest[],
  contractorId: string,
): ContractorInterest[] {
  return interests.filter(
    (i) => i.contractorId === contractorId && i.status === "open",
  );
}
